import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";

import { useAuth } from "../../../provider";
import { useTranslation } from "../../../language";
import * as c from "../../../constants";
import PageProtectedLayout from "../../../components/Page/PageProtectedLayout";
import LabWorkflowSteps from "../../../components/Steps/LabWorkflowSteps";
import LabVisitAdminConfirmCard from "./LabVisitAdminConfirmCard";

// Visit states this page is relevant for — questionnaire already submitted,
// physical visit not yet confirmed (mirrors MicrobiomePath's RELEVANT_STATES pattern)
const RELEVANT_STATES = [4];

export default function LabvisitStepAdmin() {
  const { state } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const labVisitIdFromNav = location.state?.labVisitId;

  const [visits, setVisits] = useState([]);
  const [currentVisit, setCurrentVisit] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);

  const activeVisitId = useMemo(
    () => labVisitIdFromNav ?? currentVisit?.id ?? null,
    [labVisitIdFromNav, currentVisit]
  );

  const labVisit = useMemo(() => {
    if (labVisitIdFromNav) {
      return visits.find((v) => String(v.id) === String(labVisitIdFromNav)) ?? currentVisit;
    }
    return currentVisit;
  }, [labVisitIdFromNav, visits, currentVisit]);

  // ── Fetch visit(s) ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!state?.user?.id) return;
    let mounted = true;

    const fetchVisits = async () => {
      setPageLoading(true);
      try {
        if (labVisitIdFromNav) {
          const { data } = await axios.get(
            `${c.API_URL}/api/user-lab-visits/${labVisitIdFromNav}/`
          );
          if (mounted) {
            setVisits(data ? [data] : []);
            setCurrentVisit(data ?? null);
          }
        } else {
          const { data } = await axios.get(`${c.API_URL}/api/user-lab-visits/`);
          const visitsArray = Array.isArray(data) ? data : data ? [data] : [];
          const active = visitsArray.find((v) => RELEVANT_STATES.includes(v.state)) ?? null;
          if (mounted) {
            setVisits(visitsArray);
            setCurrentVisit(active);
          }
        }
      } catch (err) {
        console.error("Failed to fetch lab visit:", err);
      } finally {
        if (mounted) setPageLoading(false);
      }
    };

    fetchVisits();
    return () => { mounted = false; };
  }, [state?.user?.id, labVisitIdFromNav]);

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (!labVisit || !activeVisitId) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <Typography variant="subtitle1" color="text.secondary">
          No active lab visit found. Please start a new lab visit.
        </Typography>
      </Box>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <PageProtectedLayout headline={t("lab_header9")} specify={t("lab_header10")}>
      <Grid
        container
        spacing={1}
        sx={{ maxWidth: 1000, mb: 16, display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        {/* ── Workflow Stepper ── */}
        <Grid size={12} sx={{ maxWidth: 800, my: 2, display: "flex", justifyContent: "center", alignItems: "center" }}>
          <LabWorkflowSteps stepNumber={3} />
        </Grid>

        <Grid size={{ xs: 12 }} sx={{ mt: 6, px: { xs: 2, sm: 0 } }}>
          <LabVisitAdminConfirmCard
            labVisitId={activeVisitId}
            confirmState={5}
            instructionsText={t("labvisitstepadmin_complete_instructions")}
            onConfirmed={() => navigate("/AccountLabVisits")}
          />
        </Grid>
      </Grid>
    </PageProtectedLayout>
  );
}
